import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { server } from "./server";
import axios from "axios";
import { toast } from "react-toastify";
import Loading from "./Components/global/Loading";
import UnAuthorized from "./Components/global/UnAuthorized";

const AuthContext = createContext({});

export const Authentication = ({ children }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [roles, setRoles] = useState([]);
  const [userPermissions, setUserPermissions] = useState([]);
  const [token, setToken] = useState(null);
  const [loadingInitial, setLoadingInitial] = useState(true);

  const fetchUserPermissions = useCallback(async (authToken) => {
    const activeToken = authToken || token || localStorage.getItem("token");
    if (!activeToken) return;
    try {
      const res = await axios.get(`${server}/permissions/my-permissions`, {
        headers: { Authorization: `Bearer ${activeToken}` },
      });
      if (res.data?.success && Array.isArray(res.data?.data)) {
        setUserPermissions(res.data.data);
      }
    } catch (err) {
      console.error("Failed to fetch user permissions:", err);
    }
  }, [token]);

  useEffect(() => {
    const verifyToken = localStorage.getItem("token");

    if (verifyToken) {
      const getMyProfileAndRoles = async () => {
        try {
          const [profileRes, rolesRes, permRes] = await Promise.all([
            axios.get(`${server}/auth/profile`, {
              headers: {
                Authorization: `Bearer ${verifyToken}`,
              },
            }),
            axios.get(`${server}/roles`, {
              headers: {
                Authorization: `Bearer ${verifyToken}`,
              },
            }).catch((err) => {
              console.error("Failed to fetch roles:", err);
              return null;
            }),
            axios.get(`${server}/permissions/my-permissions`, {
              headers: {
                Authorization: `Bearer ${verifyToken}`,
              },
            }).catch((err) => {
              console.error("Failed to fetch user permissions:", err);
              return null;
            }),
          ]);

          setUser(profileRes.data?.data);
          if (rolesRes?.data?.data) {
            setRoles(rolesRes.data.data);
          }
          if (permRes?.data?.success && Array.isArray(permRes?.data?.data)) {
            setUserPermissions(permRes.data.data);
          }
          setToken(verifyToken);
          setLoadingInitial(false);
        } catch (e) {
          setLoadingInitial(false);
          return console.error(e?.response?.data?.message || e?.message);
        }
      };

      getMyProfileAndRoles();
    } else {
      setLoadingInitial(false);
    }
  }, []);

  const loginWithEmailAndPassword = async (email, password) => {
    setLoading(true);
    try {
      const formData = {
        email,
        password,
      };

      const { data } = await axios.post(`${server}/auth/login`, formData);

      const userObj = data?.data;
      const userToken = data?.token;

      setUser(userObj);
      localStorage.setItem("token", userToken);
      setToken(userToken);

      try {
        const [rolesRes, permRes] = await Promise.all([
          axios.get(`${server}/roles`, {
            headers: {
              Authorization: `Bearer ${userToken}`,
            },
          }).catch(() => null),
          axios.get(`${server}/permissions/my-permissions`, {
            headers: {
              Authorization: `Bearer ${userToken}`,
            },
          }).catch(() => null),
        ]);

        if (rolesRes?.data?.data) {
          setRoles(rolesRes.data.data);
        }
        if (permRes?.data?.success && Array.isArray(permRes?.data?.data)) {
          setUserPermissions(permRes.data.data);
        }
      } catch (roleErr) {
        console.error("Failed to fetch roles or permissions on login:", roleErr);
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message);
    } finally {
      setLoading(false);
    }
  };

  const registerWithEmailAndPassword = async (
    name,
    mobile,
    email,
    password,
  ) => {
    setLoading(true);
    try {
      const formData = {
        name,
        mobile,
        email,
        password,
      };

      const { data } = await axios.post(`${server}/user/register`, formData);
      setUser(data?.user);
      localStorage.setItem("token", data?.token);
      setToken(data?.token);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message);
    } finally {
      setLoading(false);
    }
  };

  const logout = useCallback(() => {
    setLoading(true);
    try {
      setUser(null);
      setToken(null);
      setRoles([]);
      setUserPermissions([]);
      localStorage.clear();
      navigate("/login");
      setLoading(false);
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const memoValues = useMemo(
    () => ({
      user,
      roles,
      userPermissions,
      fetchUserPermissions,
      loading,
      registerWithEmailAndPassword,
      loginWithEmailAndPassword,
      logout,
      token,
    }),
    [user, roles, userPermissions, fetchUserPermissions, loading, token, logout],
  );

  if (loadingInitial) {
    return <Loading />;
  }

  // Determine user role and match against fetched system roles from /roles API
  const userRoleName =
    typeof user?.role === "object"
      ? user?.role?.name
      : user?.role || user?.userType || "";

  const userRoleId =
    user?.roleId || (typeof user?.role === "object" ? user?.role?.id : null);

  const hasRole = Boolean(userRoleName || userRoleId);

  const matchedRole =
    roles.length > 0
      ? roles.find(
          (r) =>
            (userRoleId && String(r.id) === String(userRoleId)) ||
            (userRoleName &&
              r.name?.toLowerCase() === String(userRoleName).toLowerCase()),
        )
      : null;

  // Throw unauthorized access if logged-in user lacks a role, or role is not found/inactive in system roles
  const isUnauthorized = Boolean(
    user &&
      (!hasRole ||
        (roles.length > 0 && (!matchedRole || matchedRole.isActive === false))),
  );

  if (isUnauthorized) {
    return (
      <AuthContext.Provider value={memoValues}>
        <UnAuthorized />
      </AuthContext.Provider>
    );
  }

  return (
    <AuthContext.Provider value={memoValues}>
      {!loadingInitial && children}
    </AuthContext.Provider>
  );
};

export default function useAuth() {
  return useContext(AuthContext);
}

